import { List, ListItem, Stack } from '@mui/material';
import { captureMessage } from '@sentry/react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSearchParams } from 'react-router-dom';

import { NewsCard } from '@frontend-libs/portal';

import type { NewsModel } from '@portal-clients/news';

import type { InfiniteScrollState } from '@frontend/hooks';
import { tApp } from '@frontend/locales';
import { usePortalPublicConfiguration } from '@frontend/portal-public-configuration';
import { KeyValueStore } from '@frontend/utils';

import { BottomDrawer } from '../bottom-drawer.component';
import { NewsCardSkeleton } from './news-card-skeleton.component';
import { NewsDrawerContent } from './news-drawer-content.component';
import { NewsListEmpty } from './news-list-empty.component';

const PAGE_SIZE = 10;

export interface NewsListProps {
  loadNext(): void;
  infiniteScrollState: InfiniteScrollState<NewsModel>;
  newIndicatorEnabled?: boolean;
}

export const NewsList = ({
  infiniteScrollState,
  loadNext,
  newIndicatorEnabled,
}: NewsListProps) => {
  const [showNews, setShowNews] = useState(false);
  const [newsToShow, setNewsToShow] = useState<NewsModel | undefined>(
    undefined,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const { portalId } = usePortalPublicConfiguration();

  const { ref: inViewRef } = useInView({
    onChange: (inView) => {
      if (inView) {
        infiniteScrollState?.hasNext && loadNext();
      }
    },
  });

  useEffect(() => {
    const newsIdParam = searchParams.get('newsId');

    if (!newsIdParam) {
      return;
    }

    const news = infiniteScrollState.currentData?.results.find(
      (newsPost) => newsPost.id === newsIdParam,
    );

    if (news) {
      setNewsToShow(news);
    } else {
      captureMessage(
        'News ID was provided from searchParams but the news was never found in the infiniteScrollState',
        {
          level: 'warning',
        },
      );
    }
  }, [infiniteScrollState?.currentData]);

  useEffect(() => {
    if (newsToShow) {
      setShowNews(true);
    }
  }, [newsToShow]);

  const ListSkeletonItems = useMemo(() => {
    if (!infiniteScrollState.currentData) {
      /*
       * Display a fixed amount of skeleton items when no data is available.
       * This should be enough to give some scrolling movement, but not too
       * much to avoid scroll jumps in case the payload is small.
       */
      return (
        <Fragment key="empty">
          <NewsCardSkeleton />
          <NewsCardSkeleton />
          <NewsCardSkeleton />
          <NewsCardSkeleton />
        </Fragment>
      );
    } else if (infiniteScrollState.hasNext) {
      /*
       * Get the amount of skeleton items to display based on the
       * remaining items in the list using the page size to limit
       * the amount of items to display.
       */
      const data = infiniteScrollState.currentData!;
      const itemsLeft = Math.min(
        PAGE_SIZE,
        data.totalCount - data.results.length,
      );

      return (
        <Fragment key={data.nextPageToken ?? 'last'}>
          {Array.from({ length: itemsLeft }, (_, index) => (
            <NewsCardSkeleton key={index} />
          ))}
        </Fragment>
      );
    }

    // No more items to load display nothing
    return undefined;
  }, [
    infiniteScrollState.loading,
    infiniteScrollState.currentData?.nextPageToken,
  ]);

  const showSkeleton = useMemo(() => {
    return infiniteScrollState.loading || !infiniteScrollState.hasNext;
  }, [infiniteScrollState.loading, infiniteScrollState.hasNext]);

  return (
    <>
      {newsToShow && (
        <BottomDrawer
          openState={showNews}
          toggleDrawer={() => {
            setShowNews(false);
          }}
          onCloseTransitionFinished={() => {
            setNewsToShow(undefined);

            if (searchParams.has('newsId')) {
              searchParams.delete('newsId');
              setSearchParams(searchParams, { replace: true });
            }
          }}
          content={<NewsDrawerContent news={newsToShow} />}
        />
      )}

      <Stack spacing={2} mt={2}>
        <List disablePadding>
          {infiniteScrollState?.currentData?.results.map((news, index) => {
            const isLast =
              infiniteScrollState.currentData!.results.length - 1 === index;

            return (
              <ListItem
                ref={isLast ? inViewRef : undefined}
                key={news.id}
                sx={{ py: 1.25 }}
              >
                <NewsCard
                  title={news.title}
                  description={news.description}
                  publishDate={news.publishDate}
                  portalId={portalId}
                  imageUrl={news.image ? news.image.imageUrl : news.imageUrl}
                  imageAltText={news.image ? news.image.altText : ''}
                  newIndicatorEnabled={newIndicatorEnabled}
                  getLastReadDate={() =>
                    KeyValueStore.get(`portal_news_datetime_${portalId}`)
                  }
                  onClick={() => {
                    if (!newsToShow) {
                      setNewsToShow(() => {
                        return infiniteScrollState?.currentData?.results?.find(
                          (post) => news.id === post.id,
                        );
                      });
                    }
                  }}
                  ariaLabel={`news_card_${index}`}
                />
              </ListItem>
            );
          })}

          {infiniteScrollState?.currentData?.results.length === 0 && (
            <NewsListEmpty
              title={tApp.news.noNewsCurrently()}
              description={tApp.news.noNewsCurrentlyDescription()}
            />
          )}

          {showSkeleton && ListSkeletonItems}
        </List>
      </Stack>
    </>
  );
};
